/**
 * Stock disponible document (PDF / shared) — aligné LogicielCaisseTransfertDetailComponent :
 * suggestions transfert (stock_disponible_depart) + repli gestion globale LC.
 */

import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LogicielCaisseAuthService } from '../logiciel-caisse/logiciel-caisse-auth.service';
import {
    LogicielCaisseProduitService,
    ProduitTransfertSuggestionGroup,
} from '../logiciel-caisse/logiciel-caisse-produit.service';
import { LogicielCaisseStockGestionService } from '../logiciel-caisse/logiciel-caisse-stock-gestion.service';
import {
    normalizePdfDocumentTypeTitre,
    pdfIsBonDeSortieDocumentType,
    pdfIsBonReceptionDocumentType,
    pdfIsStockLcDocumentType,
    pdfShouldFilterStockByEntrepotType,
    pickLcGlobaleStockQtyForDocumentType,
} from './document-pdf-totals.util';

export const LC_ENTREPOT_HIDDEN_KEY = 'logiciel_caisse_entrepot_id';

export type LcStockMeta = {
    id: number;
    code_barre?: string | null;
    ref_interne?: string | null;
    ref_fabricant?: string | null;
};

export function parseDocumentHiddenFournisseurFields(doc: any): Record<string, unknown> | null {
    const raw = doc?.hidden_fournisseur_fields;
    if (!raw) {
        return null;
    }
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as Record<string, unknown>;
        } catch {
            return null;
        }
    }
    if (typeof raw === 'object') {
        return raw as Record<string, unknown>;
    }
    return null;
}

/** Id entrepôt logiciel caisse pour filtrer le stock (pas l’id ERP brut). */
export function getLcEntrepotIdFromDocument(doc: any): number | null {
    const hidden = parseDocumentHiddenFournisseurFields(doc);
    const fromHidden = Number(hidden?.[LC_ENTREPOT_HIDDEN_KEY]);
    if (Number.isFinite(fromHidden) && fromHidden > 0) {
        return fromHidden;
    }
    const ent = doc?.entrepot;
    if (ent && typeof ent === 'object') {
        const lc = Number(
            (ent as any).lc_id ?? (ent as any).logiciel_caisse_id ?? (ent as any).id_lc ?? (ent as any).id
        );
        if (Number.isFinite(lc) && lc > 0) {
            return lc;
        }
    }
    // Repli (aligné updateStockForBonReception) : entrepot_id peut être l’id LC.
    const fromDocEntrepot = Number(doc?.entrepot_id ?? doc?.entrepot?.id);
    if (Number.isFinite(fromDocEntrepot) && fromDocEntrepot > 0) {
        return fromDocEntrepot;
    }
    return null;
}

export function buildLcStockMetaFromDocumentLigne(ligne: any): LcStockMeta | null {
    const pid = Number(ligne?.product_id);
    if (!Number.isFinite(pid) || pid <= 0) {
        return null;
    }
    const refInterneRaw =
        ligne?.ref_interne ??
        (ligne?.reference && ligne.reference !== '-' && ligne.reference !== '--' ? ligne.reference : null);
    return {
        id: pid,
        code_barre: ligne?.code_bar ?? ligne?.code_barre ?? null,
        ref_interne: refInterneRaw != null ? String(refInterneRaw).trim() : null,
        ref_fabricant: ligne?.ref_fabricant ?? ligne?.ref_fabriquant ?? null,
    };
}

export function collectLcStockMetasFromDocumentLignes(doc: any): LcStockMeta[] {
    const lignes = doc?.lignesDocument || doc?.lignes_document || [];
    if (!Array.isArray(lignes)) {
        return [];
    }
    const metaById = new Map<number, LcStockMeta>();
    for (const l of lignes) {
        const meta = buildLcStockMetaFromDocumentLigne(l);
        if (meta && !metaById.has(meta.id)) {
            metaById.set(meta.id, meta);
        }
    }
    return [...metaById.values()];
}

/** Même construction que TransfertDetailComponent.hydrate (stockMap). */
export function stockDisponibleMapFromTransfertSuggestionGroups(
    groups: ProduitTransfertSuggestionGroup[] | null | undefined
): Record<number, number> {
    const out: Record<number, number> = {};
    for (const g of groups || []) {
        for (const v of g.variants || []) {
            const pid = Number(v.product_id);
            if (!Number.isFinite(pid) || pid <= 0) {
                continue;
            }
            if (v.stock_disponible_depart != null) {
                out[pid] = Math.trunc(Number(v.stock_disponible_depart) || 0);
            }
        }
    }
    return out;
}

export function pdfShouldShowQuantiteDisponibleForDocument(doc: any): boolean {
    return pdfIsStockLcDocumentType(normalizePdfDocumentTypeTitre(doc));
}

export function pdfShouldFilterStockByEntrepotForDocument(doc: any): boolean {
    return pdfShouldFilterStockByEntrepotType(normalizePdfDocumentTypeTitre(doc));
}

/**
 * Charge quantités disponibles par product_id (logiciel caisse), comme le détail transfert entrepôt.
 */
export function loadDocumentStockDisponibleByProductIdLc(
    doc: any,
    lcAuth: LogicielCaisseAuthService,
    lcStock: LogicielCaisseStockGestionService,
    lcProduit: LogicielCaisseProduitService
): Observable<Record<number, number>> {
    if (!pdfShouldShowQuantiteDisponibleForDocument(doc)) {
        return of({});
    }
    if (!pdfIsStockLcDocumentType(normalizePdfDocumentTypeTitre(doc))) {
        return of({});
    }

    const metas = collectLcStockMetasFromDocumentLignes(doc);
    if (!metas.length) {
        return of({});
    }

    const lcEntrepotId = getLcEntrepotIdFromDocument(doc);
    const entrepotIds =
        pdfShouldFilterStockByEntrepotForDocument(doc) && lcEntrepotId != null
            ? [lcEntrepotId]
            : undefined;
    const typeTitre = normalizePdfDocumentTypeTitre(doc);
    // Bon réception : stock à l’entrepôt de réception (gestion globale), pas stock_disponible_depart (transfert).
    const useDisponibleSuggestions =
        !pdfIsBonDeSortieDocumentType(typeTitre) && !pdfIsBonReceptionDocumentType(typeTitre);

    return lcAuth.ensureAuthenticatedSession().pipe(
        switchMap((ok) => {
            if (!ok) {
                return of({});
            }
            return lcAuth.ensureLcContextIds();
        }),
        switchMap((ids) => {
            if (!ids) {
                return of({});
            }

            const loadGlobale = (targets: LcStockMeta[]) =>
                lcStock.getStockGlobaleByErpProducts(targets, { entrepotIds }).pipe(
                    map((rec) => {
                        const out: Record<number, number> = {};
                        for (const m of targets) {
                            out[m.id] = pickLcGlobaleStockQtyForDocumentType(typeTitre, rec[m.id]);
                        }
                        return out;
                    }),
                    catchError(() => of({} as Record<number, number>))
                );

            if (lcEntrepotId != null && useDisponibleSuggestions) {
                return lcProduit.getTransfertSuggestions(lcEntrepotId).pipe(
                    map((groups) => stockDisponibleMapFromTransfertSuggestionGroups(groups)),
                    switchMap((fromSuggestions) => {
                        const missing = metas.filter((m) => fromSuggestions[m.id] === undefined);
                        if (!missing.length) {
                            return of(fromSuggestions);
                        }
                        return loadGlobale(missing).pipe(
                            map((glob) => ({ ...fromSuggestions, ...glob }))
                        );
                    }),
                    catchError(() => loadGlobale(metas))
                );
            }

            return loadGlobale(metas);
        }),
        catchError(() => of({}))
    );
}
